// Usage-over-time (admin JWT). Shape mirrors the API exactly:
//   - UsageHistoryReport / UsageHistoryPoint → api/src/services/usage-history-service.ts
//   - the route                              → api/src/http/routes/admin.ts (GET /admin/usage/history?days=1..90)
//
// Channels are authoritative; agents is null for any day the relay count was
// unknown (relay link off) — render as "unknown", never a fabricated number.
// Success bodies are Zod-validated so envelope drift fails loud as a ContractError.
import { z } from "zod";
import { apiFetch } from "./client";

export interface UsageHistoryPoint {
  /** UTC calendar date, "YYYY-MM-DD". */
  date: string;
  channels: number;
  agents: number | null;
  tier: string;
}

export interface UsageHistoryReport {
  rangeDays: number;
  /** One point per snapshotted day, ascending. Sparse — no zero-fill. */
  points: UsageHistoryPoint[];
}

const pointSchema = z.object({
  date: z.string(),
  channels: z.number(),
  agents: z.number().nullable(),
  tier: z.string(),
});
const reportSchema = z.object({ rangeDays: z.number(), points: z.array(pointSchema) });

/** Daily usage history for the caller's org. `days` clamps 1..90 (API default 30). */
export function getUsageHistory(days?: number, signal?: AbortSignal): Promise<UsageHistoryReport> {
  const q = days ? `?days=${encodeURIComponent(days)}` : "";
  return apiFetch(`/admin/usage/history${q}`, { auth: true, signal, schema: reportSchema });
}
